import { createInertiaApp, Head, router, usePage, type ResolvedComponent } from '@inertiajs/react'
import { StrictMode, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { pushVirtualPageView } from '../utils/gtm'
import { installReloadOnPreloadError } from '../utils/reloadOnStaleAsset'
import FavoriteSyncProvider from '../components/FavoriteSyncProvider'
import { FORCE_INDICES_ARRAY_FORMAT_IN_FORM_DATA } from "@/constants/inertiaForm"

const DEFAULT_INERTIA_TITLE = 'くふうmarry'

type InertiaMetaTagItem = {
  tagName: string
  headKey: string
  type?: string
  innerContent?: string
  name?: string
  property?: string
  content?: string
  charset?: string
}

interface SharedPageProps {
  noindex?: boolean
  _inertia_meta?: InertiaMetaTagItem[]
}

function SharedHead() {
  const { noindex, _inertia_meta } = usePage<SharedPageProps>().props

  return (
    <Head>
      <meta
        head-key="robots"
        name="robots"
        content={noindex ? 'noindex' : 'index,follow'}
      />
      {(_inertia_meta ?? []).map(({ tagName, headKey, type, innerContent, ...attrs }) => {
        if (tagName === 'title') {
          return <title key={headKey} head-key={headKey}>{innerContent}</title>
        }
        if (tagName === 'script') {
          return (
            <script
              key={headKey}
              head-key={headKey}
              type={type}
              dangerouslySetInnerHTML={{ __html: innerContent ?? '' }}
            />
          )
        }
        return <meta key={headKey} head-key={headKey} {...attrs} />
      })}
    </Head>
  )
}

// 全ページを eager に同梱するとサイト全体のコードが初回表示で必要になるため、
// 動的 import にして、開いたページのチャンクだけを取得する。
//
// components / sections 配下はページではなく部品なので、glob から外している。
// 含めると部品ごとに別チャンクになり、1 ページを開くたびに取得するチャンクが増える。
// この配下に Inertia のページを置くと解決できなくなる（Missing Inertia page component になる）。
// 逆に、この外に部品を置くとページと同じく個別のチャンクになる。
const pages = import.meta.glob<{ default: ResolvedComponent }>([
  '../pages/**/*.tsx',
  '!../pages/**/*.test.tsx',
  '!../pages/**/*.spec.tsx',
  '!../pages/**/components/**',
  '!../pages/**/sections/**',
])

installReloadOnPreloadError()

void createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  title: (title) => title || DEFAULT_INERTIA_TITLE,

  // Disable progress bar
  //
  // see https://inertia-rails.dev/guide/progress-indicators
  // progress: false,

  resolve: async (name) => {
    const loadPage = pages[`../pages/${name}.tsx`]
    if (!loadPage) {
      throw new Error(`Missing Inertia page component: '${name}.tsx'`)
    }

    const page = await loadPage()

    // 全ページ共通でお気に入りの自動同期を起動する。各ページが独自レイアウトを
    // 持つため、DOM を包まない FavoriteSyncProvider を既定レイアウトとして差し込む。
    page.default.layout ||= (pageEl: ReactNode) => (
      <FavoriteSyncProvider>
        <SharedHead />
        {pageEl}
      </FavoriteSyncProvider>
    )

    return page
  },

  setup({ el, App, props }) {
    // Inertia 2.x は初回表示でも navigate を発火するため、初回分もここで計測される。
    // setup で別途 push すると初回の page_view が二重に記録される。
    // ただし戻る/進むは、Inertia の履歴の復元のされ方によって、計測されないことも
    // 二重に計測されることもある（例: bfcache からの復元や、履歴を復元できず replace で
    // 再取得した場合は計測されない。Inertia を経由せず history.replaceState で URL だけ
    // 書き換えたページへ、遷移先でリロードしてから戻った場合は二重になる）。
    // 重複除けを入れると同じ URL への正当な再表示まで落とすため、この誤差は許容している。
    router.on('navigate', (event) => {
      queueMicrotask(() => pushVirtualPageView(event.detail.page.url))
    })

    createRoot(el).render(
      <StrictMode>
        <App {...props} />
      </StrictMode>
    )
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: FORCE_INDICES_ARRAY_FORMAT_IN_FORM_DATA,
    },
    future: {
      useScriptElementForInitialPage: true,
      useDataInertiaHeadAttribute: true,
      useDialogForErrorModal: true,
      preserveEqualProps: true,
    },
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
      "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
      'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
    )
  }
})
